/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: BUSL-1.1
 */

import {
  HdsCardContainer,
  HdsTextDisplay,
  HdsTextBody,
  HdsTable,
  HdsIcon,
  HdsPaginationCompact,
  HdsTooltipButton,
  HdsLinkStandalone,
  type HdsTableColumn,
} from '@hashicorp/design-system-components/components';
// import { on } from '@ember/modifier';
import FilterBar, { type FilterFieldDefinition } from './filter-bar.gts';
import './inventory-table.scss';
import Component from '@glimmer/component';
import { type Filter } from '../../utils/filters.ts';
import { service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';

export type InventoryTableColumn = HdsTableColumn & {
  cellFormatter?: (data: Record<string, unknown>) => string;
};

export type QuickFilter = {
  label: string;
  tooltip?: string;
  getParams: () => { filters?: Filter[] };
  count?: number;
};

export interface InventoryTableSignature {
  Args: {
    onFilterApplied: (value: Filter[]) => void;
    onSortApplied: (sort: string[]) => void;
    onPageChange: (pagination: object) => void;
    onPageSizeChange?: (pageSize: number) => void;
    pageSize?: number;
    nextPageToken?: string | null;
    previousPageToken?: string;
    appliedFilters: Filter[];
    appliedSort: string[];
    filterFieldDefinitions: FilterFieldDefinition[];
    columns: InventoryTableColumn[];
    rows: Record<string, unknown>[];
    quickFilters: QuickFilter[];
    isLoading?: boolean;
    isError?: boolean;
    totalUnfilteredCount: number;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLElement;
}

export default class InventoryTable extends Component<InventoryTableSignature> {
  @service declare readonly router: RouterService;

  handleApplyFilters = (filters: Filter[]) => {
    this.args.onFilterApplied(filters);
  };

  handleSort = (sortBy: string, sortOrder: 'asc' | 'desc') => {
    // This method can be used to handle sorting logic if needed
    // Currently, it does not perform any action
    if (this.args.onSortApplied) {
      this.args.onSortApplied([`${sortBy}:${sortOrder}`]);
    }
  };

  handlePageChange = (direction: string) => {
    // construct the pagination object based on the direction
    let pagination;
    if (direction === 'next') {
      pagination = {
        next_page_token: this.args.nextPageToken,
        previous_page_token: null,
        page_size: this.args.pageSize || 10,
      };
    } else {
      pagination = {
        next_page_token: null,
        previous_page_token: this.args.previousPageToken,
        page_size: this.args.pageSize || 10,
      };
    }
    this.args.onPageChange(pagination);
  };

  handlePageSizeChange = (pageSize: number) => {
    if (this.args.onPageSizeChange) {
      this.args.onPageSizeChange(pageSize);
    } else {
      const pagination = {
        next_page_token: null,
        previous_page_token: null,
        page_size: pageSize,
      };
      this.args.onPageChange(pagination);
    }
  };

  get isDisabledNext() {
    return !this.args.nextPageToken || this.args.isLoading;
  }

  get isDisabledPrev() {
    return !this.args.previousPageToken || this.args.isLoading;
  }

  get routeName() {
    return this.router.currentRouteName || '';
  }

  getData = (key: string = '', row: Record<string, unknown> = {}) => {
    const column = this.args.columns.find((col) => col.key === key);
    let value = row?.[key] as string;
    if (column?.cellFormatter) {
      try {
        value = column.cellFormatter(row);
      } catch (error) {
        // no-op if formatter fails
        console.error(`Error formatting cell for key "${key}":`, error);
      }
    }
    return value ?? '-';
  };

  getParams = (quickFilter: QuickFilter) => {
    const params = quickFilter?.getParams();
    return params;
  };

  hasCount = (quickFilter: QuickFilter) => {
    return (
      quickFilter?.count !== undefined &&
      quickFilter?.count !== null &&
      !isNaN(quickFilter.count)
    );
  };

  get sortBy() {
    type sort = { key: string; order: 'asc' | 'desc' };
    const sort = this.args?.appliedSort?.[0];
    if (!sort) return { key: '', order: 'asc' } as sort;
    const [key, order] = sort.split(':');

    if (!order || !['asc', 'desc'].includes(order)) {
      return { key, order: 'asc' } as sort;
    }

    return { key, order } as sort;
  }

  get rows() {
    if (this.args.isLoading || this.args.isError) {
      return [{}];
    }
    return this.args.rows || [];
  }

  <template>
    <div class="inventory" ...attributes>
      {{#if @quickFilters}}
        <HdsTextBody
          @tag="h2"
          @size="200"
          @weight="semibold"
          @color="faint"
        >Quick filters</HdsTextBody>
        <div class="inventory__quick-filters">
          {{#each @quickFilters as |quickFilter|}}
            <HdsCardContainer
              class="inventory__quick-filter"
              @hasBorder={{true}}
              {{! @glint-expect-error }}
              @level="mid"
              {{! @glint-expect-error }}
              @background="neutral-secondary"
              data-test-vault-reporting-inventory-table-quick-filter={{quickFilter.label}}
            >
              <div class="inventory__quick-filter-header">
                <HdsTextDisplay
                  @tag="h3"
                  @size="100"
                  @weight="medium"
                  @color="medium"
                  data-test-vault-reporting-inventory-table-quick-filter-title
                >
                  {{quickFilter.label}}
                </HdsTextDisplay>
                {{#if quickFilter.tooltip}}
                  <HdsTooltipButton
                    @text={{quickFilter.tooltip}}
                    aria-label="More information"
                  >
                    <HdsIcon @name="info" />
                  </HdsTooltipButton>
                {{/if}}
              </div>
              <HdsTextDisplay
                @tag="p"
                @size="300"
                @weight="bold"
                data-test-vault-reporting-inventory-table-quick-filter-count
              >
                {{#if (this.hasCount quickFilter)}}
                  {{quickFilter.count}}
                  /
                  {{@totalUnfilteredCount}}
                {{else}}
                  - / -
                {{/if}}
              </HdsTextDisplay>

              <HdsLinkStandalone
                @icon="filter"
                @iconPosition="leading"
                @text="Apply filter"
                @color="secondary"
                @route={{this.routeName}}
                @query={{this.getParams quickFilter}}
                class="inventory__quick-filter-link"
                data-test-vault-reporting-inventory-table-quick-filter-apply
              />
            </HdsCardContainer>
          {{/each}}
        </div>
      {{/if}}

      <FilterBar
        @onFiltersApplied={{this.handleApplyFilters}}
        @appliedFilters={{@appliedFilters}}
        @filterFieldDefinitions={{@filterFieldDefinitions}}
      />

      <HdsTable
        @columns={{@columns}}
        @model={{this.rows}}
        @onSort={{this.handleSort}}
        @sortBy={{this.sortBy.key}}
        @sortOrder={{this.sortBy.order}}
        data-test-vault-reporting-inventory-table
      >
        <:body as |B|>
          {{#if @isLoading}}
            <B.Tr>
              <B.Td colspan={{@columns.length}}>
                <div class="inventory__loading">
                  <HdsIcon @name="loading" @size="24" @color="primary" />
                  <HdsTextDisplay>Loading...</HdsTextDisplay>
                </div>
              </B.Td>
            </B.Tr>
          {{else if @isError}}
            <B.Tr>
              <B.Td colspan={{@columns.length}}>
                <div class="inventory__error">
                  <HdsIcon @name="alert-circle" @size="24" @color="primary" />
                  <HdsTextDisplay>Error fetching data!</HdsTextDisplay>
                </div>
              </B.Td>
            </B.Tr>
          {{else}}
            <B.Tr data-test-vault-reporting-inventory-table-row>
              {{#each @columns as |column|}}
                <B.Td
                  data-test-vault-reporting-inventory-table-cell={{column.key}}
                >
                  {{this.getData column.key B.data}}
                </B.Td>
              {{/each}}
            </B.Tr>
          {{/if}}
        </:body>
      </HdsTable>
      <HdsPaginationCompact
        class="vault-reporting-inventory-table__pagination-bar"
        @onPageChange={{this.handlePageChange}}
        @isDisabledPrev={{this.isDisabledPrev}}
        @isDisabledNext={{this.isDisabledNext}}
        @showSizeSelector={{true}}
        @onPageSizeChange={{this.handlePageSizeChange}}
      />
    </div>
  </template>
}
